import { Code, ConnectError } from '@connectrpc/connect'

const NON_RETRYABLE_ERROR_CODES = [Code.PermissionDenied, Code.NotFound, Code.AlreadyExists]

function shouldRetryError(error: unknown): boolean {
  if (error instanceof ConnectError) {
    return !NON_RETRYABLE_ERROR_CODES.includes(error.code)
  }
  return true
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export type RetryOptions = {
  maxRetries: number
  baseDelayMs: number
  shouldRetry?: (error: unknown) => boolean
}

/**
 * Retries a function with exponential backoff.
 * Delays: baseDelayMs, baseDelayMs * 2, baseDelayMs * 4, ...
 */
export async function retryWithBackoff<T>(fn: () => Promise<T>, options: RetryOptions): Promise<T> {
  const { maxRetries, baseDelayMs, shouldRetry = shouldRetryError } = options
  let lastError: unknown

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error

      if (attempt === maxRetries || !shouldRetry(error)) {
        throw error
      }

      const delayMs = baseDelayMs * Math.pow(2, attempt)
      await sleep(delayMs)
    }
  }

  throw lastError
}

/**
 * Executes an array of task functions with a concurrency limit.
 * Returns results in the same order as the input tasks.
 */
export async function runWithConcurrencyLimit<T>(
  tasks: (() => Promise<T>)[],
  limit: number,
  onProgress?: (completed: number, total: number) => void,
): Promise<T[]> {
  const results: T[] = new Array(tasks.length)
  let completed = 0
  let nextIndex = 0

  async function runNext(): Promise<void> {
    while (nextIndex < tasks.length) {
      const index = nextIndex
      nextIndex++

      const result = await tasks[index]()
      results[index] = result
      completed++
      onProgress?.(completed, tasks.length)
    }
  }

  const workers = Array.from({ length: Math.min(limit, tasks.length) }, () => runNext())
  await Promise.all(workers)

  return results
}

export type SettledResult<T> = {
  successes: T[]
  failures: { item: T; error: unknown }[]
}

/**
 * Like Promise.allSettled but with a concurrency cap. Wraps runWithConcurrencyLimit
 * (which throws on first error) so callers can keep going on partial failure
 * and report what went wrong per item.
 */
export async function settleWithConcurrencyLimit<T>(
  items: T[],
  fn: (item: T) => Promise<unknown>,
  limit: number,
): Promise<SettledResult<T>> {
  type Outcome = { ok: true; item: T } | { ok: false; item: T; error: unknown }

  const tasks = items.map((item) => async (): Promise<Outcome> => {
    try {
      await fn(item)
      return { ok: true, item }
    } catch (error) {
      return { ok: false, item, error }
    }
  })

  const outcomes = await runWithConcurrencyLimit(tasks, limit)

  const successes: T[] = []
  const failures: { item: T; error: unknown }[] = []
  for (const outcome of outcomes) {
    if (outcome.ok) successes.push(outcome.item)
    else failures.push({ item: outcome.item, error: outcome.error })
  }
  return { successes, failures }
}
